import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BurnedLicense, LicenseProduct } from '@tekman/onmat/models';
import { Observable } from 'rxjs';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { environment } from '../../../../../../../apps/onmat-zero/src/environments/environment';

/** Programs whose licenses grant access to ONMAT. */
const ONMAT_PROGRAM = 'ONMAT';
const EMAT_PROGRAM = 'EMAT';
/** Region whose license flow requires burning the code in Tekman Digital. */
const ESP_REGION = 'ESP';
/** Tekman Digital page where the student burns their license code. */
const ACTIVATION_PATH = 'home/activate';

@Injectable({ providedIn: 'root' })
export class BurnedLicensesService {
  /**
   * ONMAT lives on its own backend (`api-*.onmat.net`), but burned licenses are
   * served by the core, so `ApiService` cannot be used here.
   */
  private readonly coreApiV7 = environment.coreApiV7;

  constructor(private readonly http: HttpClient) {}

  /**
   * Returns the licenses already burned/activated by the authenticated user.
   * An empty array means the user has no active licenses.
   * The Keycloak bearer token is attached by `KeycloakBearerInterceptor`.
   */
  getBurnedByUser(): Observable<BurnedLicense[]> {
    return this.http.get<BurnedLicense[]>(`${this.coreApiV7}/license/user/burned-licenses`);
  }

  /**
   * Whether the license activation (burn) flow applies to the user. Only ESP
   * users burn, and that is told apart by the academic year id of their license
   * catalog (e.g. `2026-ESP`); every other region has all its licenses already
   * active and never burns codes.
   */
  isEspAcademicYear(academicYearId: string): boolean {
    return (academicYearId ?? '').toUpperCase().includes(ESP_REGION);
  }

  /**
   * Canonical level token of a course or of a license level: keeps the school
   * grade and drops the language suffix, e.g. the student's `courseId`
   * `ESO1-ESP` and the license level `ESO1` both yield `ESO1`.
   */
  private levelToken(value: string): string {
    return (value ?? '').trim().split('-')[0].toUpperCase();
  }

  /**
   * Whether a burned-license product grants ONMAT: any `ONMAT` or `EMAT`
   * license does. The `stage` is deliberately ignored, since the level match in
   * `hasOnmatLicense` already pins the product down to the student's grade.
   */
  private isOnmatProduct(product: LicenseProduct): boolean {
    return product?.programId === ONMAT_PROGRAM || product?.programId === EMAT_PROGRAM;
  }

  /**
   * Whether any burned license grants ONMAT for the student's own course.
   *
   * `userLevel` is their SSO `courseId` (e.g. `ESO1-ESP`): holding an ONMAT
   * license is not enough, it must be the one for their level, since an `ESO2`
   * license grants no access to `ESO1`. A student with no course (e.g. a
   * STUDENT_TMP) has no level to match against, so any license counts.
   */
  hasOnmatLicense(licenses: BurnedLicense[], userLevel?: string): boolean {
    const level = this.levelToken(userLevel);
    return (licenses ?? []).some(license =>
      (license?.license ?? []).some(product => this.isOnmatProduct(product) && (!level || this.levelToken(product?.level) === level))
    );
  }

  /**
   * Takes the student out of ONMAT and over to Tekman Digital to burn their
   * code: the activation (burn) flow lives there, not in this app. It is an
   * external redirect, so the SPA is left behind.
   */
  redirectToActivation(): void {
    window.location.href = `${environment.tekmanDigital}${ACTIVATION_PATH}`;
  }
}
